import { useTranslation } from "@redotech/redo-i18n";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left_filled.svg";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import classnames from "classnames";
import { memo } from "react";
import { RedoSingleSelectDropdownInput } from "../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoButton } from "../buttons/redo-button";
import * as styles from "./redo-table-pagination.module.css";

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100, 200, 500];

export const redoTablePaginationSize = ["xs", "sm", "md", "lg", "xl"] as const;
export type RedoTablePaginationSize = (typeof redoTablePaginationSize)[number];

export const RedoTablePagination = memo(function RedoTablePagination({
  currentPage,
  pageSize,
  knownRowCount,
  setCurrentPage,
  setPageSize,
  pageSizes = DEFAULT_PAGE_SIZES,
  size = "md",
  trueTotalKnown = true,
  loading = false,
  sticky = false,
  hideTopBorder = false,
  cardLayout = false,
  showWhenSinglePage = false,
  pageSizeLabel,
}: {
  currentPage: number;
  pageSize: number;
  knownRowCount: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  pageSizes?: readonly number[];
  size?: RedoTablePaginationSize;
  /** When false, `totalRows` is taken to mean the total number of
   * rows that are known to exist, and the next page button will
   * allow moving past the last page if the current page is not loading.
   */
  trueTotalKnown?: boolean;
  loading?: boolean;
  sticky?: boolean;
  hideTopBorder?: boolean;
  /** Connect pagination to a standalone/card RedoTable (side + bottom borders). */
  cardLayout?: boolean;
  /** Keep the bar visible (for the count and page-size selector) even
   * when everything fits on a single page. */
  showWhenSinglePage?: boolean;
  pageSizeLabel?: string;
}) {
  const { t } = useTranslation();
  const firstRowOnPage = (currentPage - 1) * pageSize + 1;
  const lastRowOnPage = knownRowCount
    ? Math.min(currentPage * pageSize, knownRowCount)
    : undefined;
  const knownPageCount = Math.ceil(knownRowCount / pageSize);

  if (trueTotalKnown && knownPageCount <= 1 && !showWhenSinglePage) {
    return null;
  }

  const textColor = knownRowCount ? "primary" : "disabled";

  return (
    <Flex
      align="center"
      bgColor="secondary"
      borderColor="secondary"
      borderStyle="solid"
      borderTopWidth={hideTopBorder || cardLayout ? "none" : "1px"}
      className={classnames(
        styles.paginationContainer,
        cardLayout && styles.cardLayout,
      )}
      data-arbiter="RedoTablePagination"
      gap="none"
      justify="space-between"
      pb="xl"
      pt="lg"
      px="3xl"
      style={{
        position: sticky ? "sticky" : "relative",
        bottom: sticky ? 0 : undefined,
      }}
      w="full"
    >
      <Flex justify="flex-start">
        <Text fontSize={size} textColor={textColor}>
          {t("{{first}} - {{last}} of {{total}}", {
            first: firstRowOnPage,
            last: lastRowOnPage ?? "?",
            total: knownRowCount && trueTotalKnown ? knownRowCount : "?",
          })}
        </Text>
      </Flex>
      <Flex gap="md" justify="center">
        <RedoButton
          aria-label={t("Previous page")}
          disabled={currentPage <= 1}
          hierarchy="tertiary"
          IconLeading={ArrowLeftIcon}
          onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
          size={size}
        />
        <Flex align="center" gap="md">
          <RedoSingleSelectDropdownInput
            disabled={!knownPageCount}
            fitToAnchor={false}
            options={Array.from({ length: knownPageCount }).map((_, index) => ({
              id: index + 1,
              type: "text",
              text: `${index + 1}`,
              value: index + 1,
            }))}
            optionSelected={({ item }) => setCurrentPage(item.value)}
            selectedItem={{
              id: currentPage,
              type: "text",
              text: `${currentPage}`,
              value: currentPage,
            }}
          />
          <Text fontSize={size} textColor={textColor}>
            {t("of {{pageCount}}", {
              context: "pagination page count",
              pageCount:
                knownPageCount && trueTotalKnown ? knownPageCount : "?",
            })}
          </Text>
        </Flex>
        <RedoButton
          aria-label={t("Next page")}
          disabled={
            // When the true total is unknown, we need to allow
            // moving one page past the end.
            currentPage >= knownPageCount && (trueTotalKnown || loading)
          }
          hierarchy="tertiary"
          IconLeading={ArrowRightIcon}
          onClick={() => {
            if (trueTotalKnown) {
              setCurrentPage(Math.min(currentPage + 1, knownPageCount));
            } else {
              setCurrentPage(currentPage + 1);
            }
          }}
          size={size}
        />
      </Flex>
      <Flex align="center" gap="md" justify="flex-end">
        <RedoSingleSelectDropdownInput<number>
          fitToAnchor={false}
          flexGrow={false}
          options={pageSizes.map((size) => ({
            id: size,
            type: "text",
            text: `${size}`,
            value: size,
          }))}
          optionSelected={({ item }) => setPageSize(item.value)}
          selectedItem={{
            id: pageSize,
            type: "text",
            text: `${pageSize}`,
            value: pageSize,
          }}
        />
        <Text fontSize={size} textColor={textColor}>
          {pageSizeLabel ?? t("results per page")}
        </Text>
      </Flex>
    </Flex>
  );
});
